import type { NostrEvent } from '@nostrify/nostrify';
import {
  LONG_FORM_MIN_CHARS,
  NOSTR_REF_RE,
  URL_RE,
  getNoteDocument,
  getNoteImage,
  getNoteProse,
  getNoteVideo,
} from '@/lib/noteUtils';
import { deriveNoteTagSlugs } from '@/lib/noteTags';

/**
 * Which records belong in the archive feed.
 *
 * The archive is for records, not for pointers to them, so a note that is only
 * a short line plus a link (to a web page, or to another Nostr note that may no
 * longer resolve) is left out. A note is KEPT when any of these is true:
 *
 *   1. It carries a picture or a video.
 *   2. It has no link at all (plain writing is never treated as a link note).
 *   3. It links to a document file: PDF, Word, text (a record, not a web page).
 *   4. It is long-form writing (see LONG_FORM_MIN_CHARS in noteUtils.ts).
 *   5. It reads like a pager intercept, or like a Bitcoin file / inscription.
 *
 * Anything else that leans on a link is dropped. Curated (pinned) notes skip
 * this filter entirely; see useArchiveTagCounts.
 *
 * One rule sits above all of these: a note with an ord.io link is always
 * hidden, whatever its date.
 *
 * This is a clean-up of the archive's BACK CATALOGUE only. Notes published from
 * FILTER_CUTOFF onward are never filtered: whatever the archive posts from now
 * on is shown as posted.
 *
 * The filter only decides what is SHOWN. Nothing is deleted: every event stays
 * in the saved cache, so loosening a rule here brings its notes straight back.
 */

/**
 * The first moment that counts as "moving forward": 3 October 2026, 00:00 UTC.
 * Notes created before this are checked by the rules below; notes created at or
 * after it are always kept. Move it later to clean up more of the back
 * catalogue, or earlier to let more through.
 */
export const FILTER_CUTOFF = Date.UTC(2026, 9, 3) / 1000;

/** Wording that marks a Bitcoin file: OP_RETURN data, inscription ids, ordinals. */
const BITCOIN_FILE_RE =
  /\bOP_RETURN\b|\b[0-9a-f]{64}i\d+\b|\binscri(?:bed|ption)s?\b|\bordinals?\b|\bmempool\.space\/tx\b/i;

/**
 * A link to ord.io, on the site itself or any subdomain of it. Matched on the
 * host, so prose that merely mentions "ord.io", or a host like "record.io",
 * does not count. (Links to other explorers such as ordinals.com or
 * ordiscan.com are deliberately NOT part of this rule.)
 */
const ORD_LINK_RE = /https?:\/\/(?:[\w-]+\.)*ord\.io(?![\w-])/i;

/** Does the note link to ord.io, in its text or in any tag? */
export function hasOrdLink(event: NostrEvent): boolean {
  if (ORD_LINK_RE.test(event.content ?? '')) return true;
  return event.tags.some((tag) => tag.some((value) => ORD_LINK_RE.test(value)));
}

/**
 * Notes the curator has chosen to hide, by full event id (hex). A hidden note is
 * never shown in the archive, whatever its date and even if it is pinned. Nothing
 * is deleted: the event stays in the saved cache, and removing its id from this
 * list brings it straight back. Add one id per line, with a note saying which.
 */
export const HIDDEN_NOTE_IDS: ReadonlySet<string> = new Set([
  // note15z0g48u2jxzuvvzrz72vgazad9uzgzxgrhmqpye8mlrrsd3yphxqm906l4
  'a09e8a9f8a9185c630431794c4745d69782408c81df6009327dfc63836240dcc',
]);

/** Has the curator hidden this note by id? */
export function isHiddenNote(event: NostrEvent): boolean {
  return HIDDEN_NOTE_IDS.has(event.id);
}

/** Should this note appear in the archive? (Pinned notes are exempt; see above.) */
export function keepNote(event: NostrEvent): boolean {
  // RULE: a note the curator hid by id is never shown.
  if (isHiddenNote(event)) return false;

  // RULE: a note with an ord.io link is never shown, old or new. This comes
  // before the cutoff below, so it applies to everything the archive posts.
  if (hasOrdLink(event)) return false;

  // New notes are never filtered; this clean-up is for the back catalogue.
  if (event.created_at >= FILTER_CUTOFF) return true;

  // 1. Pictures and videos are always kept.
  if (getNoteVideo(event) || getNoteImage(event)) return true;

  const content = event.content ?? '';
  const links = content.match(URL_RE) ?? [];
  const refs = content.match(NOSTR_REF_RE) ?? [];

  // 2. No link of any kind: plain writing, nothing to go dead.
  if (links.length === 0 && refs.length === 0) return true;

  // 3. A document file (PDF, Word, text…) is a record in its own right.
  if (getNoteDocument(event)) return true;

  // 4. Long-form writing stays, links or not.
  if (getNoteProse(event).length >= LONG_FORM_MIN_CHARS) return true;

  // 5. Pager intercepts and Bitcoin files. Uses the same classification as the
  //    "Pager Intercepts" and "Bitcoin" collections, so the two always agree.
  if (BITCOIN_FILE_RE.test(content)) return true;
  const slugs = deriveNoteTagSlugs(event);
  if (slugs.includes('intercept') || slugs.includes('inscribed')) return true;

  return false;
}
